/* eslint-disable @typescript-eslint/restrict-template-expressions */
/* eslint-disable @typescript-eslint/no-unsafe-call */
import { animated, to, Transition } from '@react-spring/web';
import maxBy from 'lodash-es/maxBy';
import sortBy from 'lodash-es/sortBy';
import * as React from 'react';
import { connect } from 'react-redux';
import { io, Socket } from 'socket.io-client/dist/socket.io.js';

import { getData, IPathGroup, Message } from '../../api/player/Chat';
import SpeechBubbleIcon from '../../shared/components/icons/taskIcons/SpeechBubbleIcon';
import { diffSeconds } from '../../shared/dateFns';
import { closeChat, DispatchProp, openChat, State as ReduxState } from '../state';
import ChatWindow from './ChatWindow';

interface IOwnProps {
    pathGroupId: number;
}

interface IStateProps {
    chatOpen: boolean;
}

type Props = IStateProps & IOwnProps & DispatchProp;

interface State {
    online: { [index: number]: boolean };
    typing: { [index: number]: boolean };
    history: Message[] | null;
    pathGroup: IPathGroup;
    currentId: number;
    loading: boolean;
    newMessages: boolean;
}

const LAST_MESSAGE_KEY = 'LAST_MESSAGE_KEY';

function calcOnline(datetime: Date): boolean {
    return diffSeconds(new Date(), datetime) < 15;
}

function mapStateToProps(state: ReduxState): IStateProps {
    const chatOpen = state.chat.open;
    return { chatOpen };
}

class Chat extends React.Component<Props, State> {
    lastSeen: { [index: number]: Date } = {};
    onlineIntervalTimer: number;
    state: State = {
        online: {},
        typing: {},
        history: null,
        pathGroup: null,
        currentId: null,
        loading: true,
        newMessages: false,
    };
    private socket: Socket = null;
    async componentDidMount() {
        const { pathGroupId, chatOpen } = this.props;
        const data = await getData(pathGroupId);
        let newMessages = false;
        if (data.history.length > 0) {
            const lastSeen = localStorage.getItem(LAST_MESSAGE_KEY);
            const lastMessage = maxBy(data.history, item => item.datetime);
            newMessages = lastSeen != null && lastSeen < lastMessage.datetime;
        }
        this.setState({
            loading: false,
            pathGroup: data.path_group,
            currentId: data.current_id,
            history: data.history,
            newMessages,
        });
        this.socket = io('/path-group-chat');
        this.socket.on('connect', this.sendJoinRoom);
        this.socket.on('reconnect', this.sendJoinRoom);
        this.socket.on('is online', this.receiveIsOnline);
        this.socket.on('go offline', this.recieveGoOffline);
        this.socket.on('new chat message', this.receiveNewChatMessage);
        this.socket.on('is typing', this.receiveIsTyping);
        this.onlineIntervalTimer = window.setInterval(this.sendIsOnline, 10000);
        if (chatOpen) {
            this.sendIsOnline();
        }
    }

    refreshOnlineState = () => {
        this.setState(state => {
            let hasChanged = false;
            const online = {
                ...state.online,
            };
            for (const idStr of Object.keys(this.lastSeen)) {
                const id = parseInt(idStr, 10);
                const isOnline = this.lastSeen[id] ? calcOnline(this.lastSeen[id]) : false;
                if (isOnline !== online[id]) {
                    hasChanged = true;
                    online[id] = isOnline;
                }
            }
            if (hasChanged) {
                return { online };
            } else {
                return null;
            }
        });
    }

    receiveIsOnline = (data: { id: number }) => {
        this.lastSeen[data.id] = new Date();
        this.refreshOnlineState();
    }
    recieveGoOffline = (data: { id: number }) => {
        this.lastSeen[data.id] = undefined;
        this.refreshOnlineState();
    }
    receiveNewChatMessage = (data: Message) => {
        this.setState(state => {
            let history = state.history ? [...state.history] : [];
            history.push(data);
            history = sortBy(history, m => m.datetime);
            const typing = {
                ...state.typing,
                [data.person]: false,
            };
            const newMessages = this.props.chatOpen ? false : true;
            localStorage.setItem(LAST_MESSAGE_KEY, data.datetime);
            return { history, typing, newMessages };
        });
        this.receiveIsOnline({ id: data.person });
    }
    receiveIsTyping = (data: { id: number, is_typing: boolean }) => {
        this.setState(state => {
            const typing = {
                ...state.typing,
                [data.id]: data.is_typing,
            };
            return { typing };
        });
        this.receiveIsOnline({ id: data.id });
    }

    sendIsOnline = () => {
        this.refreshOnlineState();
        if (!this.socket.connected) {
            return;
        }
        if (!this.props.chatOpen) {
            return;
        }
        if (this.state.history != null && this.state.history.length > 0) {
            const lastMessage = maxBy(this.state.history, item => item.datetime);
            localStorage.setItem(LAST_MESSAGE_KEY, lastMessage.datetime);
        }
        this.setState({ newMessages: false });
        this.socket.emit('is online', {
            path_group_id: this.state.pathGroup.id,
        });
    }
    sendGoOffline = () => {
        this.socket.emit('go offline', {
            path_group_id: this.state.pathGroup.id,
        });
    }
    sendJoinRoom = () => {
        this.socket.emit('join room', {
            path_group_id: this.state.pathGroup.id,
        });
    }
    sendChatMessage = (message: string) => {
        const { pathGroup } = this.state;
        const data = {
            message,
            path_group_id: pathGroup.id,
        };
        this.socket.emit('send chat message', data);
    }
    sendIsTyping = (isTyping: boolean) => {
        const { currentId, pathGroup } = this.state;
        const data = {
            id: currentId,
            is_typing: isTyping,
            path_group_id: pathGroup.id,
        };
        this.socket.emit('change typing', data);
    }

    openChat = () => {
        this.props.dispatch(openChat());
    }
    closeChat = () => {
        this.props.dispatch(closeChat());
    }

    componentWillUnmount() {
        this.socket.disconnect();
        window.clearInterval(this.onlineIntervalTimer);
    }

    render(): JSX.Element {
        const {
            online,
            history,
            typing,
            pathGroup,
            currentId,
            loading,
            newMessages,
        } = this.state;
        const { chatOpen } = this.props;
        return <div>
            <Transition
                native
                items={chatOpen}
                from={{ scaleX: 0.25, scaleY: 0.12 }}
                enter={{ scaleX: 1.0, scaleY: 1.0 }}
                leave={{ scaleX: 0.25, scaleY: 0.12 }}
                config={{ tension: 1000, friction: 60 }}
            >
                {(style, open) => open
                    ? <animated.div
                        id='chat'
                        style={{
                            transform: to([style.scaleX, style.scaleY], (sX, sY) => `scale(${sX}, ${sY})`)
                        }}
                    >
                        <ChatWindow
                            closeChat={this.closeChat}
                            loading={loading}
                            currentId={currentId}
                            pathGroup={pathGroup}
                            online={online}
                            typing={typing}
                            messages={history}
                            sendChatMessage={this.sendChatMessage}
                            sendIsTyping={this.sendIsTyping}
                            sendIsOnline={this.sendIsOnline}
                            sendGoOffline={this.sendGoOffline}
                        />
                    </animated.div>
                    : <button
                        className='chat-button'
                        onClick={this.openChat}
                        style={{ opacity: chatOpen ? 0 : 1 }}
                    >
                        <SpeechBubbleIcon active invert alert={newMessages} />
                    </button>
                }
            </Transition>
        </div>;
    }
}

export default connect<IStateProps, object, IOwnProps>(mapStateToProps)(Chat);
